import type {
  AggregatedLocationDatum,
  EntityRecord,
  LocationDatum,
  Segment,
  TopicRecord
} from '../types';

interface KeyedMetric {
  location: Record<string, string>;
  [metric: string]: Record<string, number> | Record<string, string> | undefined;
}

export function mapKeyedMetric(
  raw: KeyedMetric,
  valueKey: string,
  limit = 50
): LocationDatum[] {
  const values = raw[valueKey] as Record<string, number>;
  return Object.keys(values)
    .map((key) => ({
      location: raw.location[key] ?? 'Unknown',
      value: Number(values[key] ?? 0)
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

interface RawAggregateMetric extends KeyedMetric {
  user_counts_left?: Record<string, number>;
  user_counts_right?: Record<string, number>;
  user_counts?: Record<string, number>;
  total_user_counts?: Record<string, number>;
  pageview_counts_left?: Record<string, number>;
  pageview_counts_right?: Record<string, number>;
  pageview_counts_neutral?: Record<string, number>;
  total_pageview_counts?: Record<string, number>;
}

export function mapAggregatedMetric(
  raw: RawAggregateMetric,
  prefix: 'user' | 'pageview',
  limit = 25
): AggregatedLocationDatum[] {
  const leftMap = raw[`${prefix}_counts_left` as const] as Record<string, number>;
  const rightMap = raw[`${prefix}_counts_right` as const] as Record<string, number>;
  const neutralKey = prefix === 'user' ? `${prefix}_counts` : `${prefix}_counts_neutral`;
  const neutralMap = raw[neutralKey as keyof RawAggregateMetric] as Record<string, number>;
  const totalMap = raw[`total_${prefix}_counts` as const] as Record<string, number>;

  return Object.keys(raw.location)
    .map((key) => ({
      location: raw.location[key] ?? 'Unknown',
      left: Number(leftMap?.[key] ?? 0),
      right: Number(rightMap?.[key] ?? 0),
      neutral: Number(neutralMap?.[key] ?? 0),
      total: Number(totalMap?.[key] ?? 0)
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

export function buildTotalsMap(raw: RawAggregateMetric, prefix: 'user' | 'pageview'): Record<string, number> {
  const neutralKey = prefix === 'user' ? `${prefix}_counts` : `${prefix}_counts_neutral`;
  const totalKey = `total_${prefix}_counts`;
  const totalMap = raw[totalKey as keyof RawAggregateMetric] as Record<string, number>;
  if (!totalMap) {
    return {};
  }
  return Object.keys(raw.location).reduce<Record<string, number>>((acc, key) => {
    const location = raw.location[key] ?? 'Unknown';
    acc[location] = Number(totalMap?.[key] ?? 0);
    return acc;
  }, {});
}

export function normalizeTopics(
  entries: { locations: Array<{ location: string; top_topics?: string[]; topics?: string[] }> }
): TopicRecord[] {
  return entries.locations.map((item) => ({
    location: item.location,
    topics: item.topics ?? item.top_topics ?? []
  }));
}

export function formatNumber(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
}

export function entityLabel(record: EntityRecord): string {
  const counts: Record<Segment, number> = {
    Left: record.left,
    Neutral: record.neutral,
    Right: record.right
  };
  return (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Neutral');
}
